import { useEffect, useState } from 'react'
import { useConfig } from 'wagmi'
import { getPublicClient } from '@wagmi/core'
import { toast } from 'sonner'
import { CONTRACT_ADDRESSES, type ContractName, getContractAddress } from '@/config/contracts'
import {
    type ContractIdentityStatus,
    EXPECTED_IDENTITY,
    type ExpectedIdentity,
    verifyContractIdentity,
} from '@/lib/contract-identity'

export interface ContractIdentityReport {
    chainId: number
    name: ContractName
    address: `0x${string}` | undefined
    status: ContractIdentityStatus
}

// Toast dedupe across hook re-mounts; the verifier's own cache dedupes probes.
const surfaced = new Set<string>()

function describe(
    expected: ExpectedIdentity,
    address: `0x${string}`,
    chainId: number,
    status: ContractIdentityStatus,
): string | null {
    switch (status.kind) {
        case 'ok':
        case 'not-configured':
            return null
        case 'no-code':
            return `No contract at ${address} on chain ${chainId} (expected ${expected.type}). The configured address is stale or wrong.`
        case 'not-identifiable':
            return `Contract at ${address} on chain ${chainId} does not answer typeAndVersion() (expected ${expected.type}). It predates the typeAndVersion() getter or is not a Blockpot contract.`
        case 'type-mismatch':
            return `Expected ${expected.type} at ${address} on chain ${chainId} — found ${status.found}.`
        case 'version-mismatch':
            return `${expected.type} at ${address} on chain ${chainId} reports ${status.found}; this build expects major version ${expected.majorVersion}.`
    }
}

// Probes every configured (chain, contract) pair once per session and
// soft-surfaces mismatches: console.table diagnostics plus a sonner toast per
// finding (error for wrong/missing/unidentifiable, warning for version
// drift). `not-configured` pairs are reported in the table but never toast —
// missing chains are routine in local dev. Reads never block on the verdict.
export function useContractIdentities() {
    const config = useConfig()
    const [statuses, setStatuses] = useState<ContractIdentityReport[]>([])

    useEffect(() => {
        let cancelled = false

        async function probe() {
            const reports: ContractIdentityReport[] = []
            for (const chainIdKey of Object.keys(CONTRACT_ADDRESSES)) {
                const chainId = Number(chainIdKey)
                const client = getPublicClient(config as never, { chainId: chainId as never })
                for (const name of Object.keys(EXPECTED_IDENTITY) as ContractName[]) {
                    const expected = EXPECTED_IDENTITY[name]
                    const address = getContractAddress(chainId, name)
                    const status = client
                        ? await verifyContractIdentity(client, address, expected)
                        : ({ kind: 'not-configured' } as const)
                    reports.push({ chainId, name, address, status })
                }
            }
            if (cancelled) return

            const findings = reports.filter(
                (r) => r.status.kind !== 'ok' && r.status.kind !== 'not-configured',
            )
            if (findings.length > 0) {
                console.error(`[contract-identity] ${findings.length} contract identity finding(s)`)
            }
            if (reports.some((r) => r.status.kind !== 'ok')) {
                console.table(
                    reports.map((r) => ({
                        chainId: r.chainId,
                        contract: r.name,
                        address: r.address ?? '—',
                        verdict: r.status.kind,
                        found:
                            'found' in r.status
                                ? r.status.found
                                : 'version' in r.status
                                  ? `${r.status.type} ${r.status.version}`
                                  : '—',
                    })),
                )
            }
            for (const r of findings) {
                if (!r.address) continue
                const dedupeKey = `${r.chainId}:${r.address}:${r.status.kind}`
                if (surfaced.has(dedupeKey)) continue
                surfaced.add(dedupeKey)
                const message = describe(EXPECTED_IDENTITY[r.name], r.address, r.chainId, r.status)
                if (!message) continue
                if (r.status.kind === 'version-mismatch') {
                    toast.warning(message)
                } else {
                    toast.error(message)
                }
            }
            setStatuses(reports)
        }

        void probe()
        return () => {
            cancelled = true
        }
    }, [config])

    return { statuses }
}
